import { useMemo, useState } from "react";

import {
  Typography,
  useBreakpointValue,
  Button,
  Flex,
  Menu,
  ItemElementProps,
} from "@nestoca/ui";
import { useGetAccount } from "@shared/api/hooks/account";
import { useGetApplicationsByApplicantId } from "@shared/api/hooks/applications";
import { DocumentStep } from "@shared/constants";
import {
  Application,
  APP_STATUSES_MAP,
  TargetProperty,
} from "@shared/constants";
import { formatAddress, useGetApplicationMetadata } from "@shared/utils";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { BsChevronDown } from "react-icons/bs";

import { ApplicationSettingsModal } from "../application-settings-modal";

import { ApplicationAddressButton } from "./application-address-button";
import { ApplicationAddressOption } from "./application-selector-option";
import styles from "./application-selector.module.scss";

import type { ApplicationWithIndex, SelectableApplication } from "./types";

export const ApplicationSelector = ({
  applicationId,
  isSmall,
}: {
  applicationId: number;
  isSmall?: boolean;
}) => {
  const isMobile = useBreakpointValue({ default: true, md: false });
  const { data: applications, isError } = useGetApplicationsByApplicantId();

  const { getRemappedTransactionType, getTransactionTypeLabel } =
    useGetApplicationMetadata();
  const { data: account } = useGetAccount();
  const { basePath, locale, replace } = useRouter();

  const { t } = useTranslation("applications");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formattedAddress = useMemo(
    () => (property: TargetProperty | undefined) => {
      return formatAddress(property?.address, "street");
    },
    []
  );

  // Reverse the array so that the most recent application is at the top
  // application order is sort in the API response by created ASC
  // so we need to reverse it to have the most recent application at the top
  // and make sure the mortgage N is always the same index and reversed
  const sortedApplications = useMemo(() => {
    if (!applications) {
      return [];
    }

    // group applications by type
    const groupedApplications = applications.reduce(
      (acc, application) => {
        const remappedTransactionType = getRemappedTransactionType(
          application.type
        );

        if (!acc[remappedTransactionType]) {
          acc[remappedTransactionType] = [];
        }

        acc[remappedTransactionType].push(application);

        return acc;
      },
      {} as Record<string, Application[]>
    );

    const items: ApplicationWithIndex[] = [];

    // then add the index number for this specific type
    Object.keys(groupedApplications).forEach((key) => {
      const applications = groupedApplications[key];

      for (let i = applications.length - 1; i >= 0; i--) {
        const application = applications[i] as ApplicationWithIndex;
        application.index = i + 1;
        // push the item to the array
        items.push(application);
      }
    });

    return items;
  }, [applications, getRemappedTransactionType]);

  const applicantId = account?.id || 0;

  if (isError || !applications) {
    return (
      <Typography className={styles["dashboard-heading"]}>
        {t("failedToLoadApplications", { ns: "common" })}
      </Typography>
    );
  }

  const selectableApplications: SelectableApplication[] =
    sortedApplications.map(
      ({ id, property, applicationState, type, index }) => {
        const applicationStatus = APP_STATUSES_MAP[applicationState];

        const applicationLink =
          basePath === "/documents"
            ? `/${locale}/${id}/applicant/${applicantId}?step=${DocumentStep.All}`
            : `/applications/${id}/applicant/${applicantId}`;

        return {
          id,
          address:
            formattedAddress(property) ||
            `${t(getTransactionTypeLabel(type))} ${index}`,
          stage: applicationStatus,
          type,
          applicantId,
          link: applicationLink,
        };
      }
    );

  const menuItems: ItemElementProps[] = selectableApplications.map(
    ({ id, address, stage, link }) => {
      return {
        id: id.toString(),
        key: id.toString(),
        title: <ApplicationAddressOption address={address} />,
        subText: t(stage),
        onClick: () => replace(link),
        isSelected: id === applicationId,
      };
    }
  );

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          onClick={() => setIsModalOpen(true)}
          className={clsx(styles["mobile-selector-button"], {
            [styles["mobile-selector-button--small"]]: isSmall,
          })}
          aria-label={"change"}
          data-dd-action-name="address"
        >
          <Flex direction="column" align="start">
            <ApplicationAddressButton />
            {!isSmall && (
              <Typography size={"00"}>{t("clickForMore")}</Typography>
            )}
          </Flex>
          <BsChevronDown />
        </Button>
        <ApplicationSettingsModal
          onClose={() => setIsModalOpen(false)}
          visible={isModalOpen}
          applicationId={applicationId}
          applicantId={applicantId}
          selectableApplications={selectableApplications}
        />
      </>
    );
  }

  return (
    <Menu
      menuItems={[{ sectionAtoms: menuItems }]}
      selectionMode="single"
      e2ePrefix="application-selector"
      allowBackgroundScroll
      buttonChildren={(isMenuOpen) => (
        <ApplicationAddressButton isMenuOpen={isMenuOpen} />
      )}
    />
  );
};
